import * as runtime from '../runtime';
import type {
  ErrorResource,
  GetPaymentStepThreeResponse,
  GetPaymentStepTwoResponse,
  MakePaymentStepOneResponse,
  MakePaymentStepThreePayload,
  MakePaymentStepThreeResponse,
  MakePaymentStepTwoPayload,
  MakePaymentStepTwoResponse,
  ValidateAddressPayload,
  ValidateAddressResponse,
} from '../models';

export interface GetPaymentStepTwoRequest {
  selectedProduct?: string;
  selectedProductRatePlan?: string;
}

export interface MakePaymentStepOneRequest {
  selectedProduct?: string;
  selectedProductRatePlan?: string;
}

export interface MakePaymentStepThreeRequest {
  makePaymentStepThreePayload: MakePaymentStepThreePayload;
}

export interface MakePaymentStepTwoRequest {
  makePaymentStepTwoPayload: MakePaymentStepTwoPayload;
}

export interface ValidateAddressRequest {
  validateAddressPayload: ValidateAddressPayload;
}

/**
 *
 */
export class PaymentApi extends runtime.BaseAPI {
  async getPaymentStepThreeRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetPaymentStepThreeResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/premium/paymentstep3`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Get Payment methods - step 3
   */
  async getPaymentStepThree(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetPaymentStepThreeResponse> {
    const response = await this.getPaymentStepThreeRaw(initOverrides);
    return await response.value();
  }

  async getPaymentStepTwoRaw(
    requestParameters: GetPaymentStepTwoRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetPaymentStepTwoResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.selectedProduct !== undefined) {
      queryParameters['selectedProduct'] = requestParameters.selectedProduct;
    }

    if (requestParameters.selectedProductRatePlan !== undefined) {
      queryParameters['selectedProductRatePlan'] =
        requestParameters.selectedProductRatePlan;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/premium/paymentstep2`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Payment flow step two
   */
  async getPaymentStepTwo(
    requestParameters: GetPaymentStepTwoRequest = {},
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetPaymentStepTwoResponse> {
    const response = await this.getPaymentStepTwoRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async makePaymentStepOneRaw(
    requestParameters: MakePaymentStepOneRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<MakePaymentStepOneResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.selectedProduct !== undefined) {
      queryParameters['selectedProduct'] = requestParameters.selectedProduct;
    }

    if (requestParameters.selectedProductRatePlan !== undefined) {
      queryParameters['selectedProductRatePlan'] =
        requestParameters.selectedProductRatePlan;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/premium/paymentstep1`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * make payment - step One
   */
  async makePaymentStepOne(
    requestParameters: MakePaymentStepOneRequest = {},
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<MakePaymentStepOneResponse> {
    const response = await this.makePaymentStepOneRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async makePaymentStepThreeRaw(
    requestParameters: MakePaymentStepThreeRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<MakePaymentStepThreeResponse>> {
    if (
      requestParameters.makePaymentStepThreePayload === null ||
      requestParameters.makePaymentStepThreePayload === undefined
    ) {
      throw new runtime.RequiredError(
        'makePaymentStepThreePayload',
        'Required parameter requestParameters.makePaymentStepThreePayload was null or undefined when calling makePaymentStepThree.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/premium/paymentstep3`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.makePaymentStepThreePayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Post user selected payment method
   */
  async makePaymentStepThree(
    requestParameters: MakePaymentStepThreeRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<MakePaymentStepThreeResponse> {
    const response = await this.makePaymentStepThreeRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async makePaymentStepTwoRaw(
    requestParameters: MakePaymentStepTwoRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<MakePaymentStepTwoResponse>> {
    if (
      requestParameters.makePaymentStepTwoPayload === null ||
      requestParameters.makePaymentStepTwoPayload === undefined
    ) {
      throw new runtime.RequiredError(
        'makePaymentStepTwoPayload',
        'Required parameter requestParameters.makePaymentStepTwoPayload was null or undefined when calling makePaymentStepTwo.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/premium/paymentstep2`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.makePaymentStepTwoPayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * make payment - step Two
   */
  async makePaymentStepTwo(
    requestParameters: MakePaymentStepTwoRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<MakePaymentStepTwoResponse> {
    const response = await this.makePaymentStepTwoRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async validateAddressRaw(
    requestParameters: ValidateAddressRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ValidateAddressResponse>> {
    if (
      requestParameters.validateAddressPayload === null ||
      requestParameters.validateAddressPayload === undefined
    ) {
      throw new runtime.RequiredError(
        'validateAddressPayload',
        'Required parameter requestParameters.validateAddressPayload was null or undefined when calling validateAddress.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/premium/address`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.validateAddressPayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Address validation
   */
  async validateAddress(
    requestParameters: ValidateAddressRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ValidateAddressResponse> {
    const response = await this.validateAddressRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
